import { ReactNode, Suspense } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { findEntry, SwitchEntry, SWITCHBOARD } from "./switchboard";
import { SwitchParamsContext } from "./useSwitchParams";
import { SuperAdminOnly } from "./SuperAdminOnly";
import { LoadingSkeleton, ToastProvider } from "../ui";
import { useDefaultLandingPage } from "../bridges/session";
import { ReportLauncherProvider } from "../visits/ReportLauncher";

/**
 * A new page, wherever it renders. Its own ToastProvider covers the legacy layout (no NextFrame above);
 * inside NextFrame it defers to the frame's provider, so there is still one toast region. The same for the report
 * launcher (2D): a new page in today's frame still opens `?report=` / `?history=`; in the new frame the frame's does.
 * Google Maps is ready here in both frames: FrameSwitch renders today's layout (and so these pages) only once Maps
 * has loaded, and NextFrame its Outlet likewise (the report's address search needs it).
 */
const Next = ({ entry }: { entry: SwitchEntry }) => {
  const Page = entry.next;
  return (
    <ToastProvider>
      <ReportLauncherProvider>
        <Suspense fallback={<div className="cy-next"><LoadingSkeleton rows={6} /></div>}><Page /></Suspense>
      </ReportLauncherProvider>
    </ToastProvider>
  );
};

const WithParams = ({ params, children }: { params: Record<string, string>; children: ReactNode }) => (
  <SwitchParamsContext.Provider value={params}>{children}</SwitchParamsContext.Provider>
);

/** The address's normal route: old or new page depending on the switchboard. */
export const SwitchRoute = ({ path, board = SWITCHBOARD }: { path: string; board?: SwitchEntry[] }) => {
  const { pathname } = useLocation();
  const home = useDefaultLandingPage();
  // Two steps: the entry by its own pattern (the route's `path`), then its params from the address actually open.
  // `path` is the route's own pattern ("/households/:id"); the parameters come from the address actually open.
  const entry = board.find((e) => e.path === path) ?? findEntry(path, board)?.entry;
  if (!entry) return <Navigate to={home} replace />;
  const params = findEntry(pathname, [entry])?.params ?? {};
  return <WithParams params={params}>{entry.mode === "next" ? <Next entry={entry} /> : entry.legacy}</WithParams>;
};

/**
 * A layout route of today's app around switched pages (today's household tabs around the edit page). While the
 * switchboard serves the NEW page at the open address, only that page renders (no old layout around it); every other
 * address under the layout, and every page still on "legacy", keeps today's layout.
 */
export const LegacyLayout = ({ layout, board = SWITCHBOARD }: { layout: ReactNode; board?: SwitchEntry[] }) => {
  const { pathname } = useLocation();
  return findEntry(pathname, board)?.entry.mode === "next" ? <Outlet /> : <>{layout}</>;
};

/** /legacy/<path> — the old page, kept for comparison until the final cleanup. */
export const LegacyRoute = ({ board = SWITCHBOARD }: { board?: SwitchEntry[] }) => {
  const rest = useParams()["*"] ?? "";
  const home = useDefaultLandingPage();
  const found = findEntry(rest, board);
  return (
    <SuperAdminOnly>
      {found ? <WithParams params={found.params}>{found.entry.legacy}</WithParams> : <Navigate to={home} replace />}
    </SuperAdminOnly>
  );
};

/** /next/<path> — preview of a new page that is still switched off. */
export const NextPreviewRoute = ({ board = SWITCHBOARD }: { board?: SwitchEntry[] }) => {
  const rest = useParams()["*"] ?? "";
  const home = useDefaultLandingPage();
  const found = findEntry(rest, board);
  return (
    <SuperAdminOnly>
      {found ? <WithParams params={found.params}><Next entry={found.entry} /></WithParams> : <Navigate to={home} replace />}
    </SuperAdminOnly>
  );
};
